/**
 * Chart-of-accounts import: dry run, then apply.
 *
 * The two calls are one act. The dry run answers a deterministic diff and a
 * `diff_hash`; the apply must carry that hash back, so an import computed
 * against one chart cannot be applied to a different one. If somebody adds an
 * account between the preview and the apply, the hash no longer matches and
 * the apply is refused — which is the whole point of previewing.
 *
 * Accounts name their parent by CODE, not by identifier, so a chart authored
 * elsewhere is portable and the client never has to invent UUIDs.
 *
 * `conflict_policy` decides what an existing code means: `reject` refuses the
 * import, `update_metadata` treats it as an update. Neither ever moves an
 * account's type or its postability silently — the diff names every changed
 * field.
 */
import type { components } from "@erp/api-client";

import { apiFetch } from "@/lib/api/http";

type Schemas = components["schemas"];

export type CoaImportAccount = Schemas["CoaImportAccount"];
export type CoaImportDiff = Schemas["CoaImportDiff"];
export type CoaImportChange = Schemas["CoaImportChange"];
export type CoaImportIssue = Schemas["CoaImportIssue"];
export type CoaImportResult = Schemas["CoaImportResult"];
export type CoaImportConflictPolicy = Schemas["CoaImportConflictPolicy"];

export function dryRunAccountImport(
  accounts: readonly CoaImportAccount[],
  conflictPolicy: CoaImportConflictPolicy,
): Promise<CoaImportDiff> {
  return apiFetch<CoaImportDiff>("/accounts/import/dry-run", {
    method: "POST",
    body: { accounts, conflict_policy: conflictPolicy },
  });
}

/** The hash is a compare-and-set: it is the preview the operator approved. */
export function importAccounts(
  accounts: readonly CoaImportAccount[],
  conflictPolicy: CoaImportConflictPolicy,
  expectedDiffHash: string,
  idempotencyKey: string,
): Promise<CoaImportResult> {
  return apiFetch<CoaImportResult>("/accounts/import", {
    method: "POST",
    body: { accounts, conflict_policy: conflictPolicy, expected_diff_hash: expectedDiffHash },
    idempotencyKey,
  });
}

export interface ParsedImport {
  accounts: CoaImportAccount[];
  /** Row-level complaints, with the 1-based line they came from. */
  problems: { line: number; message: string }[];
}

const TYPES = new Set(["asset", "liability", "equity", "income", "expense"]);
const BALANCES = new Set(["debit", "credit"]);

/**
 * Parses the pasted chart. The format is deliberately the smallest thing that
 * can express the contract's required fields, one account per line:
 *
 *   code, name, type, normal balance, postable, parent code?, currency?
 *
 * A malformed line is reported with its line number rather than dropped: an
 * import that silently skips a row is how a chart ends up missing an account
 * nobody notices until a statement will not balance.
 */
export function parseAccounts(text: string): ParsedImport {
  const accounts: CoaImportAccount[] = [];
  const problems: { line: number; message: string }[] = [];
  const lines = text.split("\n");
  for (let index = 0; index < lines.length; index += 1) {
    const raw = (lines[index] ?? "").trim();
    if (raw === "" || raw.startsWith("#")) continue;
    const cells = raw.split(",").map((cell) => cell.trim());
    const [code, name, accountType, normalBalance, postable, parentCode, currency] = cells;
    const line = index + 1;
    if (code === undefined || code === "" || name === undefined || name === "") {
      problems.push({ line, message: "code and name are required" });
      continue;
    }
    if (accountType === undefined || !TYPES.has(accountType)) {
      problems.push({ line, message: "type must be asset, liability, equity, income or expense" });
      continue;
    }
    if (normalBalance === undefined || !BALANCES.has(normalBalance)) {
      problems.push({ line, message: "normal balance must be debit or credit" });
      continue;
    }
    accounts.push({
      code,
      name,
      account_type: accountType as CoaImportAccount["account_type"],
      normal_balance: normalBalance as CoaImportAccount["normal_balance"],
      is_postable: postable === undefined ? true : postable.toLowerCase() !== "false",
      ...(parentCode === undefined || parentCode === "" ? {} : { parent_code: parentCode }),
      ...(currency === undefined || currency === ""
        ? {}
        : { currency_restriction: currency.toUpperCase() }),
    });
  }
  return { accounts, problems };
}
